import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  CreditCard,
  Trash2,
  PlusCircle,
  CheckCircle,
  Loader2,
  ArrowLeft,
  AlertCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import ConfirmationModal from "../components/Modal/ConfirmationModal";
import StripePaymentModal from "../components/Modal/StripePaymentModal";
import { Badge } from "../components/ui/badge";
import PaymentIcons from "../components/billing/PaymentIcons";
import { useDispatch, useSelector } from "react-redux";
import { openAuthDialog } from "../store/auth/authSlice";
import BillingAccessMessage from "../components/billing/BillingAccessMessage";
import { useToast } from "../components/ui/use-toast";
import {
  addPaymentMethod,
  fetchPaymentMethodsByUserId,
  removePaymentMethod,
  updateDefaultPaymentMethod,
} from "../store/paymentMethod/paymentMethodActions";
import { getUserDefaultPaymentMethod } from "../store/auth/authActions";
import {
  getEffectivePlan,
  isDowngradePending,
  getDowngradeTargetPlan,
  getTimeUntilDowngrade,
} from "../utils/subscriptionUtils";

const BillingPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const dispatch = useDispatch();

  const [isStripePaymentModalOpen, setIsStripePaymentModalOpen] =
    useState(false);
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] =
    useState(false);
  const [isDefaultConfirmModalOpen, setIsDefaultConfirmModalOpen] =
    useState(false);
  const [selectedCard, setSelectedCard] = useState(null);

  const {
    isAuthenticated,
    user,
    currentSubscription,
    isLoading: isAuthLoading,
  } = useSelector((state) => state.auth);

  const {
    isLoading: isPaymentMethodsLoading,
    paymentMethods,
    isAddPaymentMethodLoading,
    isRemovePaymentMethodLoading,
    isUpdateDefaultPaymentMethodLoading,
  } = useSelector((state) => state.paymentMethod);
  const currentPlan = getEffectivePlan(currentSubscription);
  const isSeller = isAuthenticated && user?.role === "seller";

  // Sort payment methods so default comes first
  const sortedPaymentMethods = [...paymentMethods].sort((a, b) => {
    if (a.is_default && !b.is_default) return -1;
    if (!a.is_default && b.is_default) return 1;
    return 0;
  });

  useEffect(() => {
    if (isAuthLoading) return;

    if (!isAuthenticated) {
      toast({
        title: "Authentication Required",
        description: "Please log in or sign up to access your dashboard.",
        variant: "destructive",
        duration: 5000,
      });
      dispatch(
        openAuthDialog({
          showLoginFirstOrOnSuccess: true,
          redirectPage: window.location.pathname,
          onSuccessfulAuth: () =>
            navigate("/dashboard/billing", { replace: true }),
          defaultTabOverride: "login",
        }),
      );
      return;
    }

    if (isAuthenticated && user?.role !== "seller") {
      toast({
        title: "Access Denied",
        description:
          "This billing page is for sellers only. Redirecting to your profile...",
        variant: "destructive",
        duration: 3000,
      });
      navigate(user.role === "buyer" ? "/profile" : "/");
      return;
    }
  }, [isAuthenticated, user, isAuthLoading, navigate, toast, openAuthDialog]);

  useEffect(() => {
    if (isAuthenticated && user?.uid && currentPlan !== "free") {
      dispatch(fetchPaymentMethodsByUserId(user?.uid));
    }
  }, [dispatch, isAuthenticated, user]);

  const handleAddNewCard = () => {
    setIsStripePaymentModalOpen(true);
  };

  const handlePaymentSuccess = async (paymentMethodId) => {
    if (!paymentMethodId || !user.uid) {
      toast({
        title: "Unable to add card",
        description:
          "We encountered an issue adding your card. Please try again in a moment.",
        variant: "destructive",
      });
      return;
    }
    try {
      const payload = {
        paymentMethodId,
        userId: user.uid,
      };

      await dispatch(addPaymentMethod({ payload })).unwrap();
      await dispatch(getUserDefaultPaymentMethod(user?.uid)).unwrap();

      setIsStripePaymentModalOpen(false);
    } catch (error) {
      console.error("Error adding card:", error);
    }
  };

  const openDeleteConfirmation = (card) => {
    setSelectedCard(card);
    setIsDeleteConfirmModalOpen(true);
  };

  const handleDeleteCard = async () => {
    if (!selectedCard?.id || !user?.uid) {
      toast({
        title: "Unable to remove card",
        description:
          "We couldn't process your request to remove this card. Please try again.",
        variant: "destructive",
      });
      return;
    }
    try {
      const payload = {
        paymentMethodId: selectedCard.id,
        userId: user.uid,
      };

      await dispatch(removePaymentMethod({ payload })).unwrap();
      await dispatch(getUserDefaultPaymentMethod(user?.uid)).unwrap();

      setIsDeleteConfirmModalOpen(false);
      setSelectedCard(null);
    } catch (error) {
      console.error("Error deleting card:", error);
    }
  };

  const openDefaultConfirmation = (card) => {
    setSelectedCard(card);
    setIsDefaultConfirmModalOpen(true);
  };

  const handleSetDefaultCard = async () => {
    if (!selectedCard?.id || !user?.uid) {
      toast({
        title: "Unable to update default card",
        description:
          "We couldn't process your request to set this as your default payment method.",
        variant: "destructive",
      });
      return;
    }
    try {
      const payload = {
        paymentMethodId: selectedCard.id,
        userId: user.uid,
      };

      await dispatch(updateDefaultPaymentMethod({ payload })).unwrap();
      await dispatch(getUserDefaultPaymentMethod(user?.uid)).unwrap();

      setIsDefaultConfirmModalOpen(false);
      setSelectedCard(null);
    } catch (error) {
      console.error("Error setting default card:", error);
    }
  };

  if (isAuthLoading || isPaymentMethodsLoading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!isSeller) {
    return <BillingAccessMessage isAuthenticated={isAuthenticated} />;
  }

  const pageVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.4 } },
  };

  return (
    <>
      <motion.div
        className="container py-8 px-4 min-h-screen"
        variants={pageVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={itemVariants} className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 sm:gap-6">
            <Button
              variant="outline"
              onClick={() => navigate("/dashboard")}
              className="flex sm:hidden self-start"
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Dashboard
            </Button>

            <div className="text-center sm:text-left flex-1">
              <h1 className="text-2xl sm:text-3xl md:text-4xl py-2 font-bold mb-2 bg-gradient-to-r from-primary via-purple-500 to-accent text-transparent bg-clip-text">
                Payment Methods
              </h1>
              <p className="text-sm sm:text-base md:text-lg text-muted-foreground max-w-2xl mx-auto sm:mx-0">
                Manage your payment methods and billing preferences. Your
                default payment method will be used for automatic payments.
              </p>
              {isDowngradePending(currentSubscription) && (
                <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
                  Your plan will change to{" "}
                  <strong className="capitalize">
                    {getDowngradeTargetPlan(currentSubscription)}
                  </strong>{" "}
                  in {getTimeUntilDowngrade(currentSubscription)}.
                </p>
              )}
            </div>

            <Button
              variant="outline"
              onClick={() => navigate("/dashboard")}
              className="hidden sm:flex"
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Dashboard
            </Button>
          </div>
        </motion.div>

        {sortedPaymentMethods.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Add New Card Button */}
            <motion.div variants={itemVariants}>
              <Card className="h-full flex flex-col border-2 border-dashed border-primary/30 hover:border-primary/50 transition-colors bg-secondary/20">
                <CardContent className="flex-grow flex flex-col items-center justify-center p-6">
                  <PlusCircle className="h-12 w-12 text-primary mb-4" />
                  <p className="text-base sm:text-lg font-medium text-center text-primary">
                    Add New Payment Method
                  </p>
                </CardContent>
                <CardFooter>
                  <Button
                    className="w-full"
                    onClick={handleAddNewCard}
                    disabled={isAddPaymentMethodLoading}
                  >
                    {isAddPaymentMethodLoading ? (
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    ) : (
                      <PlusCircle className="mr-2 h-5 w-5" />
                    )}
                    Add Card
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>
            {/* Existing Payment Methods */}
            {sortedPaymentMethods.map((card) => (
              <motion.div key={card.id} variants={itemVariants}>
                <Card className="h-full flex flex-col hover:shadow-primary/20 hover:shadow-lg transition-shadow relative">
                  {card.is_default && (
                    <div className="absolute -top-2 -left-2 z-10">
                      <Badge
                        variant="success"
                        className="text-xs font-semibold px-2.5 py-1 rounded-full shadow-md flex items-center"
                      >
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Default
                      </Badge>
                    </div>
                  )}

                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-xl sm:text-2xl">
                      <CreditCard className="h-5 w-5 text-primary" />
                      {card.brand.charAt(0).toUpperCase() +
                        card.brand.slice(1).toLowerCase()}{" "}
                      •••• {card.last4}
                    </CardTitle>
                    <CardDescription>
                      Expires: {card.exp_month}/{card.exp_year}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="flex-grow">
                    <div className="flex items-center justify-center py-4">
                      <div className="bg-muted rounded-lg p-3">
                        <PaymentIcons brand={card.brand} className="h-8 w-12" />
                      </div>
                    </div>
                  </CardContent>

                  <CardFooter className="flex flex-col gap-2">
                    {!card.is_default && (
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => openDefaultConfirmation(card)}
                        disabled={isUpdateDefaultPaymentMethodLoading}
                      >
                        {isUpdateDefaultPaymentMethodLoading &&
                        selectedCard?.id === card.id ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <CheckCircle className="mr-2 h-4 w-4" />
                        )}
                        Set as Default
                      </Button>
                    )}

                    <Button
                      variant="destructive"
                      className="w-full"
                      onClick={() => openDeleteConfirmation(card)}
                      disabled={isRemovePaymentMethodLoading || card.is_default}
                    >
                      {isRemovePaymentMethodLoading &&
                      selectedCard?.id === card.id ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : card.is_default ? (
                        <CreditCard className="mr-2 h-4 w-4" />
                      ) : (
                        <Trash2 className="mr-2 h-4 w-4" />
                      )}
                      {card.is_default ? "Default Card" : "Remove"}
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
            ))}
          </div>
        ) : (
          <motion.div
            variants={itemVariants}
            className="col-span-full flex flex-col items-center justify-center py-12 text-center"
          >
            <AlertCircle className="h-16 w-16 text-muted-foreground mb-4" />
            <h3 className="text-lg sm:text-xl font-semibold mb-2">
              No payment methods
            </h3>
            <p className="text-muted-foreground max-w-md mb-6">
              You haven't added any payment methods yet. Add your first payment
              method to get started.
            </p>
            <Button onClick={handleAddNewCard}>
              <PlusCircle className="mr-2 h-5 w-5" />
              Add Your First Card
            </Button>
          </motion.div>
        )}
      </motion.div>

      {/* Stripe Payment Modal for Adding New Card */}
      <StripePaymentModal
        isOpen={isStripePaymentModalOpen}
        onClose={() => {
          setIsStripePaymentModalOpen(false);
        }}
        title="Add New Payment Method"
        description="Enter your card details to add a new payment method."
        type="card"
        buttonText="Add Card"
        onPaymentSuccess={handlePaymentSuccess}
        isLoading={isAddPaymentMethodLoading}
      />

      {/* Confirmation Modal for Card Deletion */}
      <ConfirmationModal
        isOpen={isDeleteConfirmModalOpen}
        onClose={() => setIsDeleteConfirmModalOpen(false)}
        onConfirm={handleDeleteCard}
        title="Remove Payment Method"
        message="Are you sure you want to remove this payment method? This action cannot be undone."
        confirmText="Remove"
        cancelText="Cancel"
        loading={isRemovePaymentMethodLoading}
      />
      {/* Confirmation Modal for Card Default */}
      <ConfirmationModal
        isOpen={isDefaultConfirmModalOpen}
        onClose={() => setIsDefaultConfirmModalOpen(false)}
        onConfirm={handleSetDefaultCard}
        title="Set as Default Payment Method"
        message="Do you want to set this card as your default payment method?"
        confirmText="Set as Default"
        cancelText="Cancel"
        loading={isUpdateDefaultPaymentMethodLoading}
      />
    </>
  );
};

export default BillingPage;
